import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { getEvents } from '../api';
import { 
  getShareIdForEvent, 
  buildSnapshotFromEvent, 
  isFavorite as isFavoriteKey, 
  toggleFavorite as toggleFavoriteForSnapshot
} from '../utils/favorites';
import { getBlogForEvent } from '../config/blogMapping';
import '../styles/event-detail.css';
import dayjs from 'dayjs';
import { inferSalsaVidaTags, getSalsaVidaPrimaryTag, tagLabelToClassSuffix } from '../utils/eventDanceTags';

function cleanDescription(description = '') {
  return description
    .split('\n')
    .filter(line =>
      !line.includes('Map') &&
      !line.includes('Highlight this date') &&
      !line.includes('Highlight all dates') &&
      !line.includes('Link to this event') &&
      !line.includes('Update this event') &&
      !line.includes('If anything here is inaccurate')
    )
    .join('\n');
}

function normalizePrice(rawPrice) {
  if (typeof rawPrice !== 'string') return '';
  let s = rawPrice.trim();
  if (!s) return '';
  if (/not found/i.test(s)) return '';

  // Many scrapers store values like "Price$15-25" or "Price: $10"
  s = s.replace(/^price\b\s*/i, '');
  s = s.replace(/^[:\-–]\s*/, '');
  return s.trim();
}

// Helper to classify a single event for display badge (same as EventList)
function getEventDisplayType(event) {
  const eventName = (event['Event Name'] || '').toLowerCase();
  const description = (event.Description || '').toLowerCase();
  const textToSearch = `${eventName} ${description}`;
  const source = event.source || '';

  // Source-based classification first
  if (source === 'tangomango') return 'Tango';
  if (source === 'bayareafusion') return 'Fusion';
  if (source === 'salsavida') {
    return getSalsaVidaPrimaryTag(inferSalsaVidaTags(event));
  }
  if (source.startsWith('googlecalendar')) return 'Improvisation';
  if (source.startsWith('nextgenswing')) return 'West Coast Swing';
  if (source === 'omni_bachata') return 'Bachata';
  if (source === 'omni_zouk') return 'Brazilian Zouk';

  // Fallback to keyword analysis
  if ((eventName.includes('meditation') || 
      textToSearch.includes('meditation class') ||
      (textToSearch.includes('meditation') && !textToSearch.includes('meditative movement'))) &&
      !textToSearch.includes('yoga') &&
      !textToSearch.includes('vinyasa')) {
    return 'Meditation';
  }
  if ((eventName.includes('yoga') || 
      textToSearch.includes('yoga class') ||
      textToSearch.includes('vinyasa')) &&
      !eventName.startsWith('meditation')) {
    return 'Yoga';
  }
  if (textToSearch.includes('fusion') &&
      !textToSearch.includes('milonga') && 
      !textToSearch.includes('tango') && 
      !textToSearch.includes('practica') &&
      !textToSearch.includes('milonguera')) {
    return 'Fusion';
  }
  if (textToSearch.includes('milonga') || 
      textToSearch.includes('tango') || 
      textToSearch.includes('practica') ||
      textToSearch.includes('milonguera')) {
    return 'Tango';
  }
  if (textToSearch.includes('ecstatic dance')) {
    return 'Ecstatic Dance';
  }
  if (textToSearch.includes('west coast swing') || textToSearch.includes('wcs')) {
    return 'West Coast Swing';
  }
  if (textToSearch.includes('dance') && 
      !textToSearch.includes('tango') && 
      !textToSearch.includes('milonga') && 
      !textToSearch.includes('practica') && 
      !textToSearch.includes('milonguera') && 
      !textToSearch.includes('ecstatic dance') &&
      !textToSearch.includes('fusion') &&
      !eventName.includes('yoga') &&  
      !textToSearch.includes('yoga class') &&
      !eventName.includes('meditation') &&
      !textToSearch.includes('meditation class')) {
    return 'Dance';
  }
  return null;
}

// Build a safe CSS class for the badge from the display type
function getEventTypeClass(event, activeDanceType) {
  const source = event?.source || '';
  if (source === 'salsavida') {
    const tags = inferSalsaVidaTags(event);
    if (activeDanceType === 'Salsa' && tags.has('Salsa')) return 'salsa';
    if (activeDanceType === 'Bachata' && tags.has('Bachata')) return 'bachata';
    return tagLabelToClassSuffix(getSalsaVidaPrimaryTag(tags)) || 'salsa';
  }
  const t = getEventDisplayType(event);
  return tagLabelToClassSuffix(t) || 'fusion';
}

function getEventDisplayTags(event) {
  const source = event?.source || '';
  if (source === 'salsavida') return Array.from(inferSalsaVidaTags(event));
  const t = getEventDisplayType(event);
  return t ? [t] : [];
}

// Format date to "Monday, Oct 27" format
function formatDate(dateString) {
  if (!dateString) return '';
  const date = dayjs(dateString);
  if (!date.isValid()) return dateString;
  return date.format('dddd, MMM D');
}

// Extract venue name and address from location string
function parseLocation(location) {
  if (!location || location === 'TBD') {
    return { venue: 'TBD', address: null };
  }
  
  // Try to split by common separators
  const parts = location.split(/[•,]/).map(p => p.trim()).filter(p => p);
  
  if (parts.length >= 2) {
    // First part is usually venue, rest is address
    return {
      venue: parts[0],
      address: parts.slice(1).join(', ')
    };
  }
  
  // If no separator, check if it looks like an address (has numbers or street indicators)
  if (/\d/.test(location) || location.toLowerCase().includes('st') || location.toLowerCase().includes('ave')) {
    return { venue: null, address: location };
  }
  
  return { venue: location, address: null };
}

function inferVenueFromEventName(eventName = '') {
  if (!eventName) return null;
  // Common pattern: "X at Venue"
  const m = eventName.match(/\bat\s+(.+?)\s*$/i);
  return m && m[1] ? m[1].trim() : null;
}

function EventDetail() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [shareMessage, setShareMessage] = useState('');

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getEvents();
        const allEvents = (data && Array.isArray(data.data)) ? data.data : [];

        const filteredData = allEvents.filter(ev =>
          ev['Event Name'] && !ev['Event Name'].includes('No event')
        );

        const found = filteredData.find(ev => getShareIdForEvent(ev) === shareId);

        if (!found) {
          setError('This event is not available in the current dataset.');
          setEvent(null);
        } else {
          setEvent(found);
        }
      } catch (err) {
        console.error('Failed to load event details', err);
        setError('Failed to load event details. Please try again later.');
        setEvent(null);
      } finally {
        setLoading(false);
      }
    };

    if (shareId) {
      fetchEvent();
    } else {
      setError('No event specified.');
      setLoading(false);
    }
  }, [shareId]);

  // Update favorite status when event changes
  useEffect(() => {
    if (event) {
      const snapshot = buildSnapshotFromEvent(event);
      setIsFavorite(isFavoriteKey(snapshot.key));
    }
  }, [event]);

  // Scroll to top when component mounts or shareId changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [shareId]);

  // Auto-clear share feedback
  useEffect(() => {
    if (!shareMessage) return undefined;
    const t = window.setTimeout(() => setShareMessage(''), 2500);
    return () => window.clearTimeout(t);
  }, [shareMessage]);

  const handleShare = async () => {
    const url = window.location.href;
    const title = event ? event['Event Name'] : 'Event';

    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
    } catch {
      // If Web Share fails, fall back to clipboard/prompt below.
    }

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        setShareMessage('Link copied to clipboard');
        return;
      }
    } catch {
      // Ignore and fall through to prompt.
    }

    // Ultimate fallback: show a prompt so the user can copy manually.
    window.prompt('Copy this event link:', url);
  };

  const handleToggleFavorite = () => {
    if (!event) return;
    const snapshot = buildSnapshotFromEvent(event);
    const wasFav = isFavoriteKey(snapshot.key);
    toggleFavoriteForSnapshot(snapshot);
    setIsFavorite(!wasFav);
  };


  const handleBack = () => {
    // Prefer explicit "from" passed by EventList. This avoids relying on browser history
    // (which restores scroll position and may lose filter context).
    const from = location.state?.from;
    const basePath = location.pathname.includes('/event/')
      ? (location.pathname.split('/event/')[0] || '/')
      : '/';
    const fallback = `${basePath}${location.search || ''}`;

    navigate(from || fallback, { replace: true, state: { scrollToTop: true } });
  };

  if (loading) {
    return (
      <div className="event-detail-loading">
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div className="event-detail-content">
        <div className="event-detail-error">
          <p className="event-detail-error__message">{error}</p>
          <button onClick={handleBack} className="event-detail-hero__back-btn event-detail-hero__back-btn--dark">
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  const eventType = getEventDisplayType(event);
  const eventTags = getEventDisplayTags(event);
  const activeDanceType = location.state?.activeDanceType || null;
  const hideExternalSiteLink = ['West Coast Swing', 'Fusion', 'Salsa'].includes(eventType);
  const typeClass = getEventTypeClass(event, activeDanceType);
  const description = cleanDescription(event.Description || '');
  const priceText = normalizePrice(event.price);
  const hasPriceKeywords = /\b(price|cost|cover|admission|fee)\b/i.test(description);
  const hasDollarAmount = /\$\s*\d/.test(description);
  const shouldPrependPrice = Boolean(priceText) && !hasPriceKeywords && !hasDollarAmount;
  const aboutText = shouldPrependPrice
    ? `Price: ${priceText}${description ? `\n\n${description}` : ''}`
    : description;
  const formattedDate = formatDate(event.Date);
  const locationInfo = parseLocation(event.Location);
  const inferredVenue = inferVenueFromEventName(event['Event Name']);
  const hasDuplicatedVenue =
    Boolean(locationInfo.venue) &&
    Boolean(locationInfo.address) &&
    locationInfo.venue.toLowerCase() === locationInfo.address.toLowerCase();
  const venueForDisplay = inferredVenue && hasDuplicatedVenue
    ? `${inferredVenue} — ${locationInfo.venue}`
    : (locationInfo.venue || 'TBD');
  const mapsQuery = [inferredVenue, locationInfo.venue, locationInfo.address, event.Location]
    .filter(Boolean)
    .join(', ');
  const mapsHref = `https://maps.google.com/?q=${encodeURIComponent(mapsQuery)}`;
  const blogInfo = getBlogForEvent(event['Event Name']);
  const blogUrl = blogInfo
    ? `${blogInfo.blogUrl}${location.search || ''}`
    : null;

  return (
    <div className="event-detail-page">
      
      {/* Hero Section */}
      <section className={`event-detail-hero event-detail-hero--${typeClass}`}>
        <div className="event-detail-hero__container">
          <button onClick={handleBack} className="event-detail-hero__back-btn">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              <path d="M7.5 10H16.6667" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Events
          </button>

          <div className="event-detail-hero__header">
            {eventTags.length > 0 && (
              <div className="event-detail-hero__tags" aria-label={`Event type: ${eventTags.join(', ')}`}>
                {eventTags.map((label) => {
                  // Pills are always colored by their own label:
                  // Salsa = orange, Bachata = pink (even on filtered views).
                  const pillClass = tagLabelToClassSuffix(label) || typeClass;
                  return (
                    <span key={label} className={`event-detail-hero__tag event-detail-hero__tag--${pillClass}`}>
                      {label}
                    </span>
                  );
                })}
              </div>
            )}
            <h1 className="event-detail-hero__title">{event['Event Name']}</h1>
          </div>

          <div className="event-detail-hero__info-cards">
            {/* Date Card */}
            <div className="event-detail-hero__info-card">
              <svg className="event-detail-hero__info-card-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="4" width="16" height="14" rx="2" stroke="#FFFFFF" strokeWidth="1.66667"/>
                <path d="M2 8H18" stroke="#FFFFFF" strokeWidth="1.66667"/>
                <path d="M6 2V4" stroke="#FFFFFF" strokeWidth="1.66667" strokeLinecap="round"/>
                <path d="M14 2V4" stroke="#FFFFFF" strokeWidth="1.66667" strokeLinecap="round"/>
              </svg>
              <div className="event-detail-hero__info-card-content">
                <p className="event-detail-hero__info-card-label">Date</p>
                <p className="event-detail-hero__info-card-value">{formattedDate || event.Date}</p>
              </div>
            </div>

            {/* Time Card */}
            <div className="event-detail-hero__info-card">
              <svg className="event-detail-hero__info-card-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="8.33" stroke="#FFFFFF" strokeWidth="1.66667"/>
                <path d="M10 5V10L13.33 13.33" stroke="#FFFFFF" strokeWidth="1.66667" strokeLinecap="round"/>
              </svg>
              <div className="event-detail-hero__info-card-content">
                <p className="event-detail-hero__info-card-label">Time</p>
                <p className="event-detail-hero__info-card-value">{event.Time || 'TBD'}</p>
              </div>
            </div>

            {/* Venue Card */}
            <div className="event-detail-hero__info-card">
              <svg className="event-detail-hero__info-card-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 18C10 18 17 12 17 7C17 3.13401 13.866 0 10 0C6.13401 0 3 3.13401 3 7C3 12 10 18 10 18Z" stroke="#FFFFFF" strokeWidth="1.66667"/>
                <circle cx="10" cy="7" r="2.5" stroke="#FFFFFF" strokeWidth="1.66667"/>
              </svg>
              <div className="event-detail-hero__info-card-content">
                <p className="event-detail-hero__info-card-label">Venue</p>
                <p className="event-detail-hero__info-card-value">{venueForDisplay}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="event-detail-content">
        <div className="event-detail-content__container">
          {/* Left Column - Main Content */}
          <div className="event-detail-content__main">
            {/* About This Event Card */}
            <div className="event-detail-card">
              <h2 className="event-detail-card__title">About This Event</h2>
              <p className="event-detail-card__text">{aboutText || 'No description available.'}</p>
            </div>

            {/* Location Card */}
            <div className="event-detail-card event-detail-card--location">
              <h2 className="event-detail-card__title">Location</h2>
              <div className="event-detail-location">
                <svg className={`event-detail-location__icon event-detail-location__icon--${typeClass}`} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 18C10 18 17 12 17 7C17 3.13401 13.866 0 10 0C6.13401 0 3 3.13401 3 7C3 12 10 18 10 18Z" stroke="currentColor" strokeWidth="1.67"/>
                  <circle cx="10" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.67"/>
                </svg>
                <div className="event-detail-location__content">
                  {locationInfo.venue && (
                    <p className="event-detail-location__venue">
                      {inferredVenue && hasDuplicatedVenue ? inferredVenue : locationInfo.venue}
                    </p>
                  )}
                  {locationInfo.address && (
                    <a 
                      href={mapsHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="event-detail-location__address"
                    >
                      {locationInfo.address}
                    </a>
                  )}
                  {!locationInfo.venue && !locationInfo.address && (
                    <p className="event-detail-location__venue">{event.Location}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Date & Time Section with Buttons */}
          <div className="event-detail-content__sidebar">
            <div className="event-detail-sidebar__section">
              <h3 className="event-detail-sidebar__section-title">Date & Time</h3>
              <div className="event-detail-sidebar__datetime">
                {/* Date */}
                <div className="event-detail-sidebar__datetime-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2" y="4" width="16" height="14" rx="2" stroke="#62748E" strokeWidth="1.66667"/>
                    <path d="M2 8H18" stroke="#62748E" strokeWidth="1.66667"/>
                    <path d="M6 2V4" stroke="#62748E" strokeWidth="1.66667" strokeLinecap="round"/>
                    <path d="M14 2V4" stroke="#62748E" strokeWidth="1.66667" strokeLinecap="round"/>
                  </svg>
                  <span className="event-detail-sidebar__datetime-text">{formattedDate || event.Date}</span>
                </div>
                {/* Time */}
                <div className="event-detail-sidebar__datetime-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="10" cy="10" r="8.33" stroke="#62748E" strokeWidth="1.66667"/>
                    <path d="M10 5V10L13.33 13.33" stroke="#62748E" strokeWidth="1.66667" strokeLinecap="round"/>
                  </svg>
                  <span className="event-detail-sidebar__datetime-text">{event.Time || 'TBD'}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="event-detail-sidebar__buttons">
                {/* Our Review Button */}
                {blogInfo && blogUrl && (
                  <button
                    onClick={() => { window.location.href = blogUrl; }}
                    className="event-detail-sidebar__btn event-detail-sidebar__btn--review"
                    aria-label={blogInfo.blogTitle}
                  >
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M10 1L12.39 6.26L18 7.27L14 11.14L14.76 17L10 14.27L5.24 17L6 11.14L2 7.27L7.61 6.26L10 1Z" fill="white"/>
                    </svg>
                    Our Review
                  </button>
                )}

                {/* Visit Event Website Button */}
                {event.Details && event.Details.startsWith('http') && !hideExternalSiteLink && (
                  <button
                    onClick={() => window.open(event.Details, '_blank', 'noopener,noreferrer')}
                    className={`event-detail-sidebar__btn event-detail-sidebar__btn--primary event-detail-sidebar__btn--${typeClass}`}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 8.67V12.67C12 13.02 11.86 13.36 11.61 13.61C11.36 13.86 11.02 14 10.67 14H3.33C2.98 14 2.64 13.86 2.39 13.61C2.14 13.36 2 13.02 2 12.67V5.33C2 4.98 2.14 4.64 2.39 4.39C2.64 4.14 2.98 4 3.33 4H7.33" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M10 2H14V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6.67 9.33L14 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Visit Event Website
                  </button>
                )}

                {/* Share Button */}
                <button
                  onClick={handleShare}
                  className="event-detail-sidebar__btn event-detail-sidebar__btn--secondary event-detail-sidebar__btn--share"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M11.3333 5.33333C12.4379 5.33333 13.3333 4.4379 13.3333 3.33333C13.3333 2.22876 12.4379 1.33333 11.3333 1.33333C10.2288 1.33333 9.33333 2.22876 9.33333 3.33333C9.33333 4.4379 10.2288 5.33333 11.3333 5.33333Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M4.66667 9.33333C5.77124 9.33333 6.66667 8.4379 6.66667 7.33333C6.66667 6.22876 5.77124 5.33333 4.66667 5.33333C3.5621 5.33333 2.66667 6.22876 2.66667 7.33333C2.66667 8.4379 3.5621 9.33333 4.66667 9.33333Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M11.3333 14.6667C12.4379 14.6667 13.3333 13.7712 13.3333 12.6667C13.3333 11.5621 12.4379 10.6667 11.3333 10.6667C10.2288 10.6667 9.33333 11.5621 9.33333 12.6667C9.33333 13.7712 10.2288 14.6667 11.3333 14.6667Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6.43333 8.22667L9.56667 10.44" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9.56667 5.56L6.43333 7.77333" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Share this event
                </button>
                {shareMessage && (
                  <div className="event-detail-sidebar__share-feedback" role="status" aria-live="polite">
                    {shareMessage}
                  </div>
                )}

                {/* Add to Favorites Button */}
                <button
                  onClick={handleToggleFavorite}
                  className={`event-detail-sidebar__btn event-detail-sidebar__btn--secondary event-detail-sidebar__btn--favorites ${isFavorite ? 'event-detail-sidebar__btn--favorite-active' : ''}`}
                >
                  <svg
                    className="event-detail-sidebar__favorite-icon"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ color: isFavorite ? '#FF2056' : '#314158' }}
                    aria-hidden="true"
                  >
                    <path 
                      d="M8 14L7.05 13.13C3.4 9.79 1 7.58 1 4.89C1 2.68 2.68 1 4.9 1C6.14 1 7.33 1.57 8 2.47C8.67 1.57 9.86 1 11.1 1C13.32 1 15 2.68 15 4.89C15 7.58 12.6 9.79 8.95 13.13L8 14Z" 
                      stroke="currentColor" 
                      strokeWidth="1.33333" 
                      fill={isFavorite ? 'currentColor' : 'none'}
                    />
                  </svg>
                  {isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default EventDetail;
